import { cartSelectors } from '../state/cart';
import cartItemSelectors from '../state/cartItems/selectors';
import pricingSelectors from '../state/pricing/selectors';
import { noticeError } from './newRelicTracking';
import { createVariantFromProductAttrs } from '../tracking/productVariantCreator';
import {
  CART_PAGE_SECTION,
  CART_PAGE_STAGE,
  CART_PAGE_NAME,
} from '../constants/cartui';
import {
  PAYPAL_OFFERTYPE_BELOW_30,
  PAYPAL_OFFERTYPE_BETWEEN_30_AND_200,
  PAYPAL_OFFERTYPE_ABOVE_200,
  PAYPAL_MESSAGE_VARIANT_BELOW_30,
  PAYPAL_MESSAGE_VARIANT_BETWEEN_30_AND_200,
  PAYPAL_MESSAGE_VARIANT_ABOVE_200,
} from '../constants/tracking';
import { CartItemsState, CartState } from '../types';

declare global {
  interface Window {
    tracking: any;
  }
}

const cartViewedStepHeader = {
  step: 1, // checkout cart step
  label: 'Cart View', // checkout step name
  pageSection: CART_PAGE_SECTION,
  pageStage: CART_PAGE_STAGE,
  pageName: CART_PAGE_NAME,
};
const itemRemovedEventHeader = {
  label: 'Product Removed',
  pageSection: CART_PAGE_SECTION,
  pageStage: CART_PAGE_STAGE,
  pageName: CART_PAGE_NAME,
};

const productDesignEditEventHeader = {
  category: CART_PAGE_SECTION, // do we need this?
  label: 'Design Edits',
  pageSection: CART_PAGE_SECTION,
  pageStage: CART_PAGE_STAGE,
  pageName: CART_PAGE_NAME,
};

function getTax(pricing: any, fractionDigits: number) {
  const divideBy = 10 ** fractionDigits;
  return pricingSelectors.selectTax(pricing) / divideBy;
}

function getDiscountedPrice(itemPricing: any, fractionDigits: number) {
  const divideBy = 10 ** fractionDigits;
  const discountTotal = pricingSelectors.selectDiscountedTotal(itemPricing) || {
    net: 0,
    gross: 0,
  };
  const gross = discountTotal.gross / divideBy;
  const net = discountTotal.net / divideBy;
  return {
    net,
    gross,
  };
}

function getDiscountedNetPrice(pricing: any, fractionDigits: number) {
  const divideBy = 10 ** fractionDigits;
  return pricingSelectors.selectDiscountedTotal(pricing).net / divideBy;
}

function getListNetPrice(pricing: any, fractionDigits: number) {
  const divideBy = 10 ** fractionDigits;
  return pricingSelectors.selectListTotal(pricing).net / divideBy;
}

function getItemDiscountTotal(pricing: any, fractionDigits: number) {
  const divideBy = 10 ** fractionDigits;
  return pricingSelectors.selectCentsDiscountedFromList(pricing) / divideBy;
}

function getItemPricing(pricing: any, itemId: string) {
  return pricingSelectors.selectPricingDataByItemId(pricing, itemId);
}

function getCoreProductId(cartItems: {}, itemId: string) {
  return cartItemSelectors.selectProductKeyByItemId(cartItems, itemId);
}

function getFulfillmentSku(cartItems: {}, itemId: string) {
  return cartItemSelectors.selectFulfillmentSkuByItemId(cartItems, itemId);
}

function getQuantity(cartItems: {}, itemId: string) {
  return cartItemSelectors.selectQuantityByItemId(cartItems, itemId);
}

function getPurcsData(cartItems: {}, itemId: string) {
  return cartItemSelectors.selectPurcsDataByItemId(cartItems, itemId);
}

function getCartProducts(cartItems: {}, pricing: any) {
  const fractionDigits = pricingSelectors.selectFractionDigits(pricing);
  const cartLineItems = cartItemSelectors.selectAllItemIds(cartItems);

  const products = cartLineItems.map((cartItemId: string) => {
    const itemPricing = getItemPricing(pricing, cartItemId);
    const productAttributes = cartItemSelectors.selectProductAttributeList(
      cartItems,
      cartItemId
    );
    // This function composes item-level analytics data from a mix of two sources:
    //
    //  1. First-class fields the cart knows about
    //  2. Arbitrary bits of data provided in the customerOrderData.analytics field on the line item
    //
    // There are some cases where our fields need to take precedence, and some cases where values provided in
    // the metadata object need to take precedence. The order of operations of this method is very sensitive.

    const container = {
      // First put product_id in, so it can potentially be overridden by metadata someone else provided.
      product_id: getCoreProductId(cartItems, cartItemId),
      name: cartItemSelectors.selectProductNameByItemId(cartItems, cartItemId),

      // Then layer in all the metadata we were provided
      ...cartItemSelectors.selectAnalyticsDataByItemId(cartItems, cartItemId),

      // But for all other fields, our versions take precedence
      // e.g. if someone adds customOrderData.analytics.quantity, we should ignore that and override
      core_product_id: getCoreProductId(cartItems, cartItemId),
      quantity: 1, // This should always be 1...stupid segment requirements
      sales_quantity: getQuantity(cartItems, cartItemId),
      price: getDiscountedNetPrice(itemPricing, fractionDigits),
      list_price: getListNetPrice(itemPricing, fractionDigits),
      tax: getTax(itemPricing, fractionDigits),
      attributes: productAttributes,
      mcp_id: getFulfillmentSku(cartItems, cartItemId),
      design: cartItemSelectors.selectDesignDataByItemId(cartItems, cartItemId),
    };

    // Don't attempt to override the variant field if one was already provided in the metadata
    if (!container.variant) {
      const variant = createVariantFromProductAttrs(productAttributes);

      // And don't attempt to set the field if it's blank (that causes problems)
      if (variant) {
        container.variant = variant;
      }
    }
    return container;
  });

  return products;
}

function getCheckoutStepData(cart: CartState, cartItems: {}, pricing: any) {
  const products = getCartProducts(cartItems, pricing);
  return {
    checkout_id: cartSelectors.selectCartId(cart),
    currency: pricingSelectors.selectCurrency(pricing),
    ...cartViewedStepHeader,
    products,
    coupon: pricingSelectors.selectDiscountCodeName(pricing) || '',
    discount: pricingSelectors.selectCentsDiscountedFromList(pricing),
  };
}

function getItemRemovedData(
  cartItemId: string,
  groupId: string,
  cart: CartState,
  cartItems: {},
  pricing: any
) {
  const fractionDigits = pricingSelectors.selectFractionDigits(pricing);
  const itemPricing = getItemPricing(pricing, cartItemId);
  const container = {
    ...itemRemovedEventHeader,
    product_id: getCoreProductId(cartItems, cartItemId),
    name: cartItemSelectors.selectProductNameByItemId(cartItems, cartItemId),
    // If a product_id is provided in analytics_metadata, we should use that instead of our own product key
    ...cartItemSelectors.selectAnalyticsDataByItemId(cartItems, cartItemId),
    // But for all other fields in that blob, our versions take precedencecart_id: cartSelectors.selectCartId(cart),
    core_product_id: getCoreProductId(cartItems, cartItemId),
    quantity: 1, // This should always be 1...stupid segment requirements
    sales_quantity: getQuantity(cartItems, cartItemId),
    price: getDiscountedNetPrice(itemPricing, fractionDigits),
    list_price: getListNetPrice(itemPricing, fractionDigits),
    mcp_id: getFulfillmentSku(cartItems, cartItemId),
    groupId,
  };
  return container;
}

function getViewLargerImageClickedData(
  id: string,
  cart: CartState,
  cartItems: {},
  pricing: any
) {
  const fractionDigits = pricingSelectors.selectFractionDigits(pricing);
  const itemPricing = getItemPricing(pricing, id);
  const container = {
    product_id: getCoreProductId(cartItems, id),
    name: cartItemSelectors.selectProductNameByItemId(cartItems, id),
    // Allow analytics data to overwrite the fields above.
    ...cartItemSelectors.selectAnalyticsDataByItemId(cartItems, id),
    // Fields below are determined by this code.
    core_product_id: getCoreProductId(cartItems, id),
    cart_id: cartSelectors.selectCartId(cart),
    quantity: getQuantity(cartItems, id),
    price: getDiscountedPrice(itemPricing, fractionDigits),
    purcsData: getPurcsData(cartItems, id),
  };
  return container;
}

function getProductDesignEditClickedData(
  id: string,
  cart: CartState,
  cartItems: {},
  pricing: any
) {
  const container = {
    product_id: getCoreProductId(cartItems, id),
    name: cartItemSelectors.selectProductNameByItemId(cartItems, id),
    ...productDesignEditEventHeader,
    // If a product_id is provided in analytics_metadata, we should use that instead of our own product key
    ...cartItemSelectors.selectAnalyticsDataByItemId(cartItems, id),
    core_product_id: getCoreProductId(cartItems, id),
    quantity: 1, // This should always be 1...stupid segment requirements
    sales_quantity: getQuantity(cartItems, id),
    checkout_id: cartSelectors.selectCartId(cart),
    coupon: pricingSelectors.selectDiscountCodeName(pricing) || '',
    cart_id: cartSelectors.selectCartId(cart),
  };
  return container;
}

/**
 * Protect against tracking not yet being installed on page or no window present (SSR).
 * The `tracking.js` package will fire a `trackingReady` event when tracking is
 * on the page.  Each `callWhenReadyFn` is a single-shot and removed once executed.
 * We use this guard for both `tracking.track` and `tracking.page`, since they're from the same module.
 */
function onTrackingReady(callback: any) {
  if (window) {
    if (window.tracking && window.tracking.track) {
      callback();
    } else {
      const callWhenReadyFn = () => {
        callback();
        window.removeEventListener('trackingReady', callWhenReadyFn);
      };
      window.addEventListener('trackingReady', callWhenReadyFn);
    }
  }
}

/**
 * Actually send `track` event. Assumes `window.tracking.track` exists.
 */
function sendTrackingEvent(name: string, data: any) {
  try {
    window.tracking.track(name, data);
  } catch (error) {
    noticeError({ error });
    console.error(
      "[TrackingJS] Error occured when firing a 'track' event",
      error
    );
  }
}

/**
 * Actually send `page` event. Assumes `window.tracking.page` exists.
 */
function sendPageEvent(pageName: string, additionalData: any) {
  try {
    window.tracking.page(pageName, additionalData);
  } catch (error) {
    noticeError({ error });
    console.error(
      "[TrackingJS] Error occured when firing a 'page' event",
      error
    );
  }
}

/** API: Execute or queue up a 'track' call.
 * If environment variable TRACKING_OVERRIDE_BEHAVIOR === 'log' we will log the event
 *  in the console and early out before firing the actual 'track()'.
 */
export function track(name: string, data: any) {
  if (process.env.TRACKING_OVERRIDE_BEHAVIOR === 'log') {
    // log and early out
    console.log('[Mock Tracking™]: Track()', name, data);
    return;
  }
  onTrackingReady(() => sendTrackingEvent(name, data));
}

/** API: Execute or queue up a 'page' call.
 * If environment variable TRACKING_OVERRIDE_BEHAVIOR === 'log' we will log the event
 *  in the console and early out before firing the actual 'page()'.
 * Return data sent, for testing use.
 */
export function pageViewed(pageName: string, additionalData: any) {
  if (process.env.TRACKING_OVERRIDE_BEHAVIOR === 'log') {
    // log and early out for non-prod env
    console.log('[Mock Tracking™]: Page()', pageName, additionalData);
    return;
  }
  onTrackingReady(() => sendPageEvent(pageName, additionalData));
  return additionalData;
}

export function trackAnonymousShopperSignedIn(anonymousShopperId: string) {
  track('Anonymous Shopper Signed In', { anonymousId: anonymousShopperId });
}

/**
 * `trackItemsRemoved` and `prefetchItemsRemovedTrackingData` are intended to be a pair
 * of functions used together.  `trackItemsRemoved` is typically called AFTER the
 * item(s) has been successfully removed.  At this point, it is attempting to
 * fetch data (especially pricing) that may not exist.  This now splits the
 * call into 2 parts, the first collecting the data, and the second firing the event.
 */
export function prefetchItemsRemovedTrackingData(
  itemIds: string[],
  groupId: string,
  cart: CartState,
  cartItems: {},
  pricing: any
) {
  return itemIds.map((itemId: string) =>
    getItemRemovedData(itemId, groupId, cart, cartItems, pricing)
  );
}

export function trackItemsRemoved(itemsRemovedData: any) {
  itemsRemovedData.forEach((itemRemovedData: any) =>
    track('Product Removed', itemRemovedData)
  );
}

export function viewLargerImageClicked(
  itemId: string,
  cart: CartState,
  cartItems: {},
  pricing: any
) {
  track(
    'View Larger Image Clicked',
    getViewLargerImageClickedData(itemId, cart, cartItems, pricing)
  );
}

/** @see https://segment.com/docs/spec/ecommerce/v2/#coupons */

function getCouponEnteredData(couponName: string, cart: CartState) {
  return {
    cart_id: cartSelectors.selectCartId(cart),
    coupon_id: couponName,
  };
}

function getValidCouponData(couponName: string, cart: CartState, pricing: any) {
  const fractionDigits = pricingSelectors.selectFractionDigits(pricing);
  return {
    cart_id: cartSelectors.selectCartId(cart),
    coupon_id: couponName,
    coupon_name: couponName,
    currency: pricingSelectors.selectCurrency(pricing),
    discount: getItemDiscountTotal(pricing, fractionDigits),
  };
}

function getCouponDeniedData(
  couponName: string,
  cart: CartState,
  pricing: any
) {
  const data: any = getValidCouponData(couponName, cart, pricing);
  data.reason =
    pricingSelectors.selectCouponErrorMessageKey(pricing) || 'invalidPromoCode';
  return data;
}

export function couponEntered(discountCodeName: string, cart: CartState) {
  track('Coupon Entered', getCouponEnteredData(discountCodeName, cart));
}

export function couponApplied(
  discountCodeName: string,
  cart: CartState,
  pricing: any
) {
  track('Coupon Applied', getValidCouponData(discountCodeName, cart, pricing));
}

export function couponDenied(
  discountCodeName: string,
  cart: CartState,
  pricing: any
) {
  track('Coupon Denied', getCouponDeniedData(discountCodeName, cart, pricing));
}

export function couponRemoved(
  discountCodeName: string,
  cart: CartState,
  pricing: any
) {
  track('Coupon Removed', getValidCouponData(discountCodeName, cart, pricing));
}

export function productDesignEditClicked(
  itemId: string,
  cart: CartState,
  cartItems: {},
  pricing: any
) {
  track(
    'Product Design Edit Selected',
    getProductDesignEditClickedData(itemId, cart, cartItems, pricing)
  );
}

/**
 * Page View event for Cart page.
 * Note: Segment does not map any product-related information currently through to GA with this call.
 * @see "Product Funnel Tracking specification for Google Analytics" @ https://vistaprint.atlassian.net
 */
export function cartStepPageViewed(
  cart: CartState,
  cartItems: {},
  pricing: any
) {
  return pageViewed(
    'Checkout Step Viewed',
    getCheckoutStepData(cart, cartItems, pricing)
  );
}

/**
 * Tracking event for Cart page  (Checkout Step 1) viewed.
 * Note: Viewing the Cart page is considered the first step in checkout for analytics.
 * @see "Product Funnel Tracking specification for Google Analytics" @ https://vistaprint.atlassian.net
 */
export function cartStepTracked(cart: CartState, cartItems: {}, pricing: any) {
  track('Checkout Step Viewed', getCheckoutStepData(cart, cartItems, pricing));
}

// ModFather tracking support:
// ModFather expects an object with a 'trackEvent' function that takes an event.
// This adapter appends the route, which is needed by Segment, and the event
// name, which determines the Snowflake table that receives the event data.
const MOD_EVENT_NAME = 'UX_MOD_EVENT';
export const MOD_ROUTE = 'om-frontend';

const trackEvent = (event: any) => {
  if (!event || !event.eventType) {
    return;
  }
  event.route = MOD_ROUTE;
  track(MOD_EVENT_NAME, event);
};
export const modfatherTracker = { trackEvent };

const getNavigationClickedData = (
  sourcePagePath = '/c',
  destinationPagePath = '',
  ctaValue = ''
) => {
  const PAGE_ZONE = 'Cart CTAs';
  return {
    category: CART_PAGE_SECTION,
    label: `Content:${PAGE_ZONE}`,
    eventDetail: `${sourcePagePath};${destinationPagePath};${PAGE_ZONE};${ctaValue}`,
    navigationDetail: ctaValue,
    pageSection: CART_PAGE_SECTION,
    pageStage: CART_PAGE_STAGE,
    pageName: CART_PAGE_NAME,
  };
};

/**
 * Tracking event for Navigation Clicked event.
 * https://vistaprint.atlassian.net/wiki/spaces/GA/pages/2218560827/Navigation+Tracking+Framework
 */
export const trackNavigationClicked = ({
  sourcePagePath,
  destinationPagePath,
  ctaValue,
}: {
  sourcePagePath: string;
  destinationPagePath: string;
  ctaValue: string;
}) => {
  track(
    'Navigation Clicked',
    getNavigationClickedData(sourcePagePath, destinationPagePath, ctaValue)
  );
};

const getExperimentClickedData = (
  sourcePagePath: string = '/c',
  destinationPagePath: string = '',
  ctaValue: string = '',
  experimentName: string = '',
  variationName: string = '',
  itemId: string = '',
  cartItems: CartItemsState = { cartItems: {} }
) => {
  const PAGE_ZONE = 'Cart CTAs';
  return {
    label: `${CART_PAGE_SECTION} view`,
    eventDetail: `${sourcePagePath};${destinationPagePath};${PAGE_ZONE};${ctaValue}`,
    navigationDetail: ctaValue,
    pageSection: CART_PAGE_SECTION,
    pageStage: CART_PAGE_STAGE,
    pageName: CART_PAGE_NAME,
    experimentName,
    variationName,
    product_id: getCoreProductId(cartItems, itemId),
    name: cartItemSelectors.selectProductNameByItemId(cartItems, itemId),
    // Allow analytics data to overwrite the fields above.
    ...cartItemSelectors.selectAnalyticsDataByItemId(cartItems, itemId),
    // Fields below are determined by this code.
    core_product_id: getCoreProductId(cartItems, itemId),
    core_product_version: cartItemSelectors.selectProductVersionByItemId(
      cartItems,
      itemId
    ),
  };
};

/**
 * Tracks PayPal Message click event, with PayPal message vaariant derived from
 * PayPal message offerType
 * @param {*} offerType optional offerType from PayPal Message onClick()'s data.meta.offerType
 */
export function trackPayPalMessageClicked(offerType: string) {
  let paypalMessageVariant;
  if (offerType === PAYPAL_OFFERTYPE_BELOW_30) {
    // offerType for "Pay in 4 interest-free payments on purchases of $30-$1,500.",
    paypalMessageVariant = PAYPAL_MESSAGE_VARIANT_BELOW_30;
  } else if (offerType === PAYPAL_OFFERTYPE_BETWEEN_30_AND_200) {
    // offerType for "Pay in 4 interest-free payments of $(x)."
    paypalMessageVariant = PAYPAL_MESSAGE_VARIANT_BETWEEN_30_AND_200;
  } else if (offerType === PAYPAL_OFFERTYPE_ABOVE_200) {
    // offerType for "// As low as $(x)/mo."
    paypalMessageVariant = PAYPAL_MESSAGE_VARIANT_ABOVE_200;
  }

  track('PayPal Message Clicked', {
    category: 'Cart',
    label: 'PayLater Learn More',
    eventDetail: 'PayPal Modal Displayed',
    paypalMessageVariant,
  });
}

/**
 * Tracking event for Experiment Clicked event.
 * https://vistaprint.atlassian.net/wiki/spaces/GA/pages/2174910576/Experimentation+tracking+framework
 */
export const trackExperimentClicked = ({
  sourcePagePath,
  destinationPagePath,
  ctaValue,
  experimentName,
  variationName,
  itemId,
  cartItems,
}: {
  sourcePagePath: string;
  destinationPagePath: string;
  ctaValue: string;
  experimentName: string;
  variationName: string;
  itemId: string;
  cartItems: CartItemsState;
}) => {
  track(
    'Experiment Clicked',
    getExperimentClickedData(
      sourcePagePath,
      destinationPagePath,
      ctaValue,
      experimentName,
      variationName,
      itemId,
      cartItems
    )
  );
};
